// Pending-approval row lookup for `<Chat>` (fn-48.4).
//
// Ported from `session-view.tsx`'s `usePendingApprovalRow` (fn-47.4 —
// chat.tsx inherited only the gate REGION from fn-48.3, not the
// actionable row resolution; per plan-sync this task owns its own
// copy). The approval row id is a DB UUID independent of any MA event
// payload, so we resolve it by polling `listApprovals` filtered to
// this session WHILE the gate is open. A session can be
// awaiting_approval with NO approval row yet (webhook lag) — until a
// row exists callers show the awaiting note, never an actionable
// skeleton.
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePlatformAdapter } from '../context.js';
/**
 * Poll `listApprovals` (filtered client-side to this session) while
 * `active`. The matched row is STICKY while active: once a decide
 * lands the row drops from the next poll, but the rendered card must
 * keep showing its decided state until the stream's ack event clears
 * the gate (`pendingApproval` → null deactivates and resets).
 *
 * Stickiness is scoped to ONE session: a genuine `sessionId`
 * transition resets synchronously (useRef compare-on-render — .flow
 * memory: per-session-ui-state, 3 rounds), and the keep-prev branch
 * never retains a row whose `session_id` differs from the current
 * session.
 */
export function usePendingApprovalRow(inputs) {
    const adapter = usePlatformAdapter();
    const [approval, setApproval] = useState(null);
    const [nonce, setNonce] = useState(0);
    const epochRef = useRef(0);
    const refresh = useCallback(() => {
        setNonce((n) => n + 1);
    }, []);
    const { sessionId, active, intervalMs, preferredToolName } = inputs;
    // Session-id transition reset — synchronous, so the OLD session's
    // row never renders against the new session (render-time setState
    // gated on a value change is the documented adjust-on-prop-change
    // pattern; React re-renders before committing).
    const sessionIdRef = useRef(sessionId);
    if (sessionIdRef.current !== sessionId) {
        sessionIdRef.current = sessionId;
        setApproval(null);
    }
    useEffect(() => {
        if (!active) {
            setApproval(null);
            return;
        }
        void nonce; // manual-refresh trigger: re-runs the effect.
        const tick = () => {
            epochRef.current += 1;
            const epoch = epochRef.current;
            adapter
                .listApprovals({})
                .then((page) => {
                if (epochRef.current !== epoch)
                    return;
                const rows = page.data.filter((a) => a.session_id === sessionId);
                const match = (preferredToolName !== null
                    ? rows.find((a) => a.tool_name === preferredToolName)
                    : undefined) ?? rows[0];
                // Sticky: keep the last matched row when the poll loses it
                // (decided elsewhere / just decided) so the card's decided
                // state stays visible until the gate clears — but only when
                // the kept row belongs to THIS session.
                setApproval((prev) => match !== undefined
                    ? match
                    : prev !== null && prev.session_id === sessionId
                        ? prev
                        : null);
            })
                .catch(() => {
                // Keep the prior row; the next tick retries. The approval
                // surface degrades to the awaiting note, never a raw error.
            });
        };
        tick();
        const interval = intervalMs > 0 ? setInterval(tick, intervalMs) : null;
        return () => {
            epochRef.current += 1;
            if (interval !== null)
                clearInterval(interval);
        };
    }, [adapter, sessionId, active, intervalMs, nonce, preferredToolName]);
    return { approval, refresh };
}
//# sourceMappingURL=use-pending-approval-row.js.map